import { useMemo } from "react";

import { useSearchContext } from "@/features/search/context/SearchContext";
import {
  type TaxonomyRow,
  getCategoryCounts,
  getTaxonomyRows,
} from "@/features/search/utils/taxonomy.utils";

type TaxonomyRows = { path: TaxonomyRow[]; nextLevel: TaxonomyRow[] };

/**
 * The category taxonomy's rows, or null when it has nothing to show.
 *
 * Shared because two siblings need the same answer: the widget renders them, and the facet
 * panel hides its flat Category section only when they exist — otherwise a page whose tree
 * came back empty would end up with no category control at all. One hook, so the two can't
 * disagree about whether the widget is on screen.
 */
export const useTaxonomyRows = (): TaxonomyRows | null => {
  const { query } = useSearchContext();
  const taxonomy = query.data?.meta.taxonomy;
  const facets = query.data?.facets;

  return useMemo(() => {
    if (!taxonomy) {
      return null;
    }

    const rows = getTaxonomyRows(taxonomy, getCategoryCounts(facets));

    return rows.path.length || rows.nextLevel.length ? rows : null;
  }, [taxonomy, facets]);
};
